import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileImage, Printer, Share2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import IdCardV2 from '../../Component/v2/idcard/IdCardV2';

const ParticipantPublicView = () => {
    const { id } = useParams();
    const [participant, setParticipant] = useState(null);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const cardRef = useRef(null);

    // Design settings state
    const [elementStyles, setElementStyles] = useState({});
    const [globalVisibility, setGlobalVisibility] = useState({});

    useEffect(() => {
        const fetchParticipant = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/participants/public/${id}`);
                const data = await response.json();

                if (data.success) {
                    setParticipant(data.data.participant);
                    setEvent(data.data.event);

                    // Fetch design settings if needed, or rely on defaults
                    // For now, let's generate defaults based on the event
                    const styles = getDefaultElementStyles(data.data.event);
                    const visibility = getDefaultVisibility(data.data.event);

                    // If the event has saved design settings, we should fetch them too
                    // But for simplicity, we'll start with defaults. 
                    // To support saved designs, we'd need to fetch them from the design endpoint
                    // or have them included in the public participant response.
                    // Let's assumet the public participant endpoint can eventually include them, 
                    // but for now we regenerate them to ensure basic display work.

                    // Fetch real design settings
                    try {
                        const designRes = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${data.data.participant.eventId}/design`);
                        const designData = await designRes.json();
                        if (designData.success && designData.data) {
                            setElementStyles({ ...styles, ...designData.data.elementStyles });
                            setGlobalVisibility({ ...visibility, ...designData.data.visibility });
                        } else {
                            setElementStyles(styles);
                            setGlobalVisibility(visibility);
                        }
                    } catch (err) {
                        console.error("Failed to fetch design settings, using defaults", err);
                        setElementStyles(styles);
                        setGlobalVisibility(visibility);
                    }

                } else {
                    setError(data.message || 'Participant not found');
                }
            } catch (err) {
                console.error('Error fetching participant:', err);
                setError('Failed to load participant details');
            } finally {
                setLoading(false);
            }
        };

        fetchParticipant();
    }, [id]);

    const handleDownloadPNG = async (withBackground = true) => {
        if (!cardRef.current) return;

        const element = cardRef.current;
        const backgroundImage = element.querySelector('img');
        const originalDisplay = backgroundImage ? backgroundImage.style.display : null;

        if (!withBackground && backgroundImage) {
            backgroundImage.style.display = 'none';
        }

        setIsDownloading(true);

        try {
            const dataUrl = await toPng(element, {
                cacheBust: true,
                backgroundColor: null,
                quality: 1,
                pixelRatio: 3,
                skipFonts: true,
                preferredFontFormat: 'woff2'
            });

            const link = document.createElement('a');
            const fileName = getParticipantName() || 'id-card';
            link.download = `${fileName}${withBackground ? '' : '-no-bg'}.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error('Could not download the image', error);
        } finally {
            if (!withBackground && backgroundImage) {
                backgroundImage.style.display = originalDisplay;
            }
            setIsDownloading(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!cardRef.current) return;
        setIsDownloading(true);

        try {
            const dataUrl = await toPng(cardRef.current, {
                cacheBust: true,
                quality: 1,
                pixelRatio: 3,
            });

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [108, 153] // Approx 430px x 610px converted to mm
            });

            pdf.addImage(dataUrl, 'PNG', 0, 0, 108, 153);
            const fileName = getParticipantName() || 'id-card';
            pdf.save(`${fileName}.pdf`);
        } catch (error) {
            console.error('Could not generate PDF', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const getParticipantName = () => {
        if (!participant || !event) return 'Participant';
        const nameField = event.participantFields?.find(f =>
            f.fieldName.toLowerCase() === 'name' || f.fieldName.toLowerCase() === 'full name'
        );
        if (!nameField) return 'Participant';
        const d = participant.data;
        return (d?.[nameField.fieldName] ?? d?.[nameField.id]) || 'Participant';
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${event.eventName} ID Card`,
                    text: `Check out my ID card for ${event.eventName}!`,
                    url: window.location.href,
                });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            // Fallback to clipboard
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
                    <div className="text-red-500 mb-4">
                        <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Unavailable</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
                    {/* Left Side - ID Card */}
                    <div className="p-8 bg-gray-50 flex justify-center items-center md:w-1/2 border-r border-gray-200">
                        <div
                            ref={cardRef}
                            className="transform scale-90 md:scale-100 transition-transform origin-center shadow-2xl"
                            style={{ width: '430px', height: '610px' }}
                        >
                            <IdCardV2
                                participant={participant}
                                event={event}
                                elementStyles={elementStyles}
                                globalVisibility={globalVisibility}
                            />
                        </div>
                    </div>

                    {/* Right Side - Actions & Details */}
                    <div className="p-8 md:w-1/2 flex flex-col justify-center space-y-8">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{getParticipantName()}</h1>
                            <p className="text-lg text-blue-600 font-medium">{event.eventName}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-4">
                                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                    {participant.paymentStatus === 'completed' ? 'Registration Confirmed' : 'Pending Payment'}
                                </div>
                                {(() => {
                                    const cat = participant.categoryId && event.participantCategories?.find(c => c.id === participant.categoryId);
                                    return cat ? (
                                        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                            {cat.name}
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => handleDownloadPNG(true)}
                                disabled={isDownloading}
                                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10 transition-colors shadow-lg shadow-blue-200"
                            >
                                <Download className="w-5 h-5 mr-2" />
                                Download ID Card
                            </button>

                            <button
                                onClick={handleDownloadPDF}
                                disabled={isDownloading}
                                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10 transition-colors"
                            >
                                <FileImage className="w-5 h-5 mr-2" />
                                Download PDF
                            </button>

                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => handleDownloadPNG(false)}
                                    disabled={isDownloading}
                                    className="flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <FileImage className="w-4 h-4 mr-2" />
                                    No Background
                                </button>
                                <button
                                    onClick={handleShare}
                                    className="flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share Link
                                </button>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-gray-100 text-center text-sm text-gray-500">
                            <p>Show this ID card at the venue for entry.</p>
                            <p className="mt-1">Event Date: {new Date(event.eventDate).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Reuse helpers from EventDetailsV2
function getDefaultElementStyles(event) {
    const styles = {};
    let currentTop = 163;

    if (event?.participantFields) {
        event.participantFields.forEach((field) => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            if (field.fieldType === 'file' && field.fieldName.toLowerCase().includes('picture')) {
                styles[fieldKey] = { bottom: 160, left: "50%", size: 170 };
            } else {
                const fontSize = field.fieldName.toLowerCase().includes('name') ? 29 :
                    field.fieldName.toLowerCase().includes('email') ? 14 : 16;
                styles[fieldKey] = { top: currentTop, left: "50%", fontSize, color: "black", textAlign: "center" };
                currentTop += fontSize + 10;
            }
        });
    }

    styles.qrCode = { bottom: 215, left: "50%", size: 100 };
    styles.participantId = { bottom: 3, left: "50%", fontSize: 12, color: "black", textAlign: "center" };

    if (event?.eventType === 'free' && event?.participantCategories?.length > 0) {
        styles.categoryName = { bottom: 174, left: "50%", fontSize: 16, color: "black", textAlign: "center" };
    }

    if (event?.eventType === 'paid') {
        styles.ticketCategory = { bottom: 174, left: "50%", fontSize: 16, color: "black", textAlign: "center" };
        styles.ticketDates = { bottom: 102, left: "50%", fontSize: 17, color: "black", textAlign: "center" };
    }

    return styles;
}

function getDefaultVisibility(event) {
    const visibility = {};
    if (event?.participantFields) {
        event.participantFields.forEach(field => {
            const fieldKey = field.fieldName.toLowerCase().replace(/\s+/g, '');
            visibility[fieldKey] = true;
        });
    }
    visibility.qrCode = true;
    visibility.participantId = true;
    if (event?.eventType === 'free' && event?.participantCategories?.length > 0) {
        visibility.categoryName = true; // always show on public view
    }
    if (event?.eventType === 'paid') {
        visibility.ticketCategory = false;
        visibility.ticketDates = false;
    }
    return visibility;
}

export default ParticipantPublicView;
